<script setup>
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useEscapePriority } from 'dashboard/composables/useEscapePriority';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

defineProps({
  src: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
});

const show = defineModel({ type: Boolean, default: false });

const close = () => {
  show.value = false;
};

// Already closes on Escape itself (below) — this only tells
// ConversationHeader's global Escape handler to stand down, so Escape closes
// the photo instead of also closing the ticket underneath it.
useEscapePriority(show);

useKeyboardEvents({
  Escape: { action: () => show.value && close(), allowOnFocusedInput: true },
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center p-8 bg-n-alpha-black3 backdrop-blur-sm"
      @click.self="close"
    >
      <button
        type="button"
        class="absolute flex items-center justify-center text-white transition-colors rounded-full top-4 ltr:right-4 rtl:left-4 size-10 bg-n-alpha-black2 hover:bg-n-alpha-black3"
        @click="close"
      >
        <span class="i-lucide-x size-5" />
      </button>
      <!--
        Contact photos are usually small source images — capping to
        max-w/max-h alone never upscales past their natural size, so the
        "expanded" view looked just as small as the sidebar thumbnail.
        `scale-[2]` forces it visibly bigger regardless of the source
        resolution; the pre-scale box is capped to 45vw/45vh so the doubled
        result never exceeds ~90vw/90vh of the viewport.
      -->
      <img
        :src="src"
        :alt="name"
        class="max-w-[45vw] max-h-[45vh] rounded-lg shadow-2xl object-contain scale-[2]"
      />
    </div>
  </TeleportWithDirection>
</template>
